import {engineContext} from './context.js';

(function(global){'use strict';
const VERSION='0.1.0-alpha';

const CONTRACTS=Object.freeze({
  snapshot:Object.freeze({
    required:['version','state','fingerprint'],
    stateRequired:['scope','invalidatedConclusions','openGaps']
  }),
  invalidation:Object.freeze({
    required:['conclusionId','action'],
    allowedActions:['INVALIDATE','REVIEW_CONFLICT','REVIEW_EVIDENCE','REVALIDATE','NONE']
  }),
  researchTask:Object.freeze({
    required:['taskId','completionState'],
    allowedCompletion:['OPEN','COMPLETED','PARTIAL','BLOCKED']
  }),
  lineageSource:Object.freeze({
    required:['sourceId','verification','freshness'],
    allowedVerification:['VERIFIED','UNVERIFIED','PARTIAL','UNKNOWN'],
    allowedFreshness:['FRESH','FRESH_UNVERIFIED','AGING','STALE','UNKNOWN']
  }),
  decisionBrief:Object.freeze({
    required:['scope','whatChanged','whyItMatters','nextAction'],
    forbidden:['buy','sell','tradeSignalTrue','capitalPermissionTrue']
  })
});

function issue(code,path,message,severity){
  return Object.freeze({code,path,message,severity:severity||'ERROR'});
}

function validate(type,payload){
  const c=CONTRACTS[type];
  if(!c) return Object.freeze({valid:false,issues:Object.freeze([issue('UNKNOWN_CONTRACT',type,'Unknown integration contract')])});
  const x=payload||{},issues=[];

  for(const k of c.required||[]){
    if(!(k in x) || x[k]===undefined) issues.push(issue('MISSING_REQUIRED',k,`Missing required field: ${k}`));
  }

  if(type==='snapshot' && x.state){
    for(const k of c.stateRequired){
      if(!(k in x.state)) issues.push(issue('MISSING_STATE_FIELD',`state.${k}`,`Missing snapshot state field: ${k}`));
    }
    if(x.state.invalidatedConclusions && !Array.isArray(x.state.invalidatedConclusions))
      issues.push(issue('TYPE_MISMATCH','state.invalidatedConclusions','Expected array'));
    if(x.state.openGaps && !Array.isArray(x.state.openGaps))
      issues.push(issue('TYPE_MISMATCH','state.openGaps','Expected array'));
  }

  if(type==='invalidation' && x.action && !c.allowedActions.includes(x.action))
    issues.push(issue('INVALID_ENUM','action',`Unsupported invalidation action: ${x.action}`));

  if(type==='researchTask' && x.completionState && !c.allowedCompletion.includes(x.completionState))
    issues.push(issue('INVALID_ENUM','completionState',`Unsupported completion state: ${x.completionState}`));

  if(type==='lineageSource'){
    if(x.verification && !c.allowedVerification.includes(x.verification))
      issues.push(issue('INVALID_ENUM','verification',`Unsupported verification state: ${x.verification}`));
    if(x.freshness && !c.allowedFreshness.includes(x.freshness))
      issues.push(issue('INVALID_ENUM','freshness',`Unsupported freshness state: ${x.freshness}`));
  }

  if(type==='decisionBrief'){
    if(x.tradeSignal===true) issues.push(issue('FORBIDDEN_OUTPUT','tradeSignal','Decision brief must not grant a trade signal'));
    if(x.capitalPermission===true) issues.push(issue('FORBIDDEN_OUTPUT','capitalPermission','Decision brief must not grant capital permission'));
    const txt=JSON.stringify(x).toUpperCase();
    if(/"ACTION"\s*:\s*"BUY"/.test(txt) || /"ACTION"\s*:\s*"SELL"/.test(txt))
      issues.push(issue('FORBIDDEN_ACTION','action','BUY/SELL action is outside this contract'));
  }

  return Object.freeze({valid:issues.length===0,issues:Object.freeze(issues)});
}

function validateBatch(entries){
  const results=(Array.isArray(entries)?entries:[]).map((e,i)=>Object.freeze({
    index:i,
    type:e&&e.type||null,
    result:validate(e&&e.type,e&&e.payload)
  }));
  return Object.freeze({
    valid:results.every(x=>x.result.valid),
    failed:results.filter(x=>!x.result.valid).length,
    results:Object.freeze(results)
  });
}

function safePass(type,payload){
  const r=validate(type,payload);
  return Object.freeze({accepted:r.valid,payload:r.valid?payload:null,issues:r.issues});
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  const snap={version:'0.1',fingerprint:'abc',state:{scope:'OWNED',invalidatedConclusions:[],openGaps:[]}};
  ok('valid snapshot accepted',validate('snapshot',snap).valid===true);
  ok('snapshot missing fingerprint rejected',validate('snapshot',{version:'0.1',state:snap.state}).valid===false);
  ok('snapshot invalidated conclusions must be array',validate('snapshot',{...snap,state:{...snap.state,invalidatedConclusions:'c1'}}).valid===false);

  ok('valid invalidation action accepted',validate('invalidation',{conclusionId:'c1',action:'INVALIDATE'}).valid===true);
  ok('unknown invalidation action rejected',validate('invalidation',{conclusionId:'c1',action:'DELETE_COMPANY'}).valid===false);

  ok('valid research task accepted',validate('researchTask',{taskId:'t1',completionState:'OPEN'}).valid===true);
  ok('invalid completion state rejected',validate('researchTask',{taskId:'t1',completionState:'DONE_FOREVER'}).valid===false);

  ok('valid lineage source accepted',validate('lineageSource',{sourceId:'s1',verification:'VERIFIED',freshness:'FRESH'}).valid===true);
  ok('invalid verification state rejected',validate('lineageSource',{sourceId:'s1',verification:'TRUST_ME',freshness:'FRESH'}).valid===false);
  ok('invalid freshness state rejected',validate('lineageSource',{sourceId:'s1',verification:'VERIFIED',freshness:'ANCIENTISH'}).valid===false);

  const brief={scope:'OWNED',whatChanged:'Evidence changed',whyItMatters:'Review needed',nextAction:'Verify demand',tradeSignal:false,capitalPermission:false};
  ok('safe decision brief accepted',validate('decisionBrief',brief).valid===true);
  ok('trade signal true rejected',validate('decisionBrief',{...brief,tradeSignal:true}).valid===false);
  ok('capital permission true rejected',validate('decisionBrief',{...brief,capitalPermission:true}).valid===false);
  ok('BUY action rejected',validate('decisionBrief',{...brief,action:'BUY'}).valid===false);

  const batch=validateBatch([{type:'snapshot',payload:snap},{type:'researchTask',payload:{taskId:'x',completionState:'BROKEN'}}]);
  ok('batch reports invalid member',batch.valid===false&&batch.failed===1);

  const pass=safePass('researchTask',{taskId:'t1',completionState:'OPEN'});
  ok('safePass forwards valid payload',pass.accepted===true&&pass.payload.taskId==='t1');

  const block=safePass('researchTask',{taskId:'t1',completionState:'MAGIC'});
  ok('safePass blocks invalid payload',block.accepted===false&&block.payload===null);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={VERSION,CONTRACTS,validate,validateBatch,safePass,selfTest};
try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestEngineContractIntegrationGuard=Object.freeze(API);
})(engineContext);